import { useMemo, useRef } from 'react'
import { Box, HStack, Text } from '@chakra-ui/react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import multiMonthPlugin from '@fullcalendar/multimonth'
import listPlugin from '@fullcalendar/list'
import frLocale from '@fullcalendar/core/locales/fr'
import type { EventInput, EventClickArg } from '@fullcalendar/core'
import type { Meeting } from '../types'
import './MeetingsCalendarView.css'

interface MeetingsCalendarViewProps {
  meetings: Meeting[]
  onMeetingClick: (meetingId: string) => void
}

// Couleurs pour les différents types d'événements
const EVENT_COLORS = {
  meeting_scheduled: { bg: '#3182CE', border: '#2B6CB0' }, // blue - AG planifiée
  convocation_deadline: { bg: '#805AD5', border: '#6B46C1' }, // purple - Date limite convocation
  pv_deadline: { bg: '#DD6B20', border: '#C05621' }, // orange - Date envoi PV
}

// Nombre de jours avant l'AG pour la convocation (21 jours minimum légal)
const CONVOCATION_DAYS_BEFORE = 21
// Nombre de jours après l'AG pour le PV (délai courant)
const PV_DAYS_AFTER = 15

export function MeetingsCalendarView({
  meetings,
  onMeetingClick,
}: MeetingsCalendarViewProps) {
  const calendarRef = useRef<FullCalendar>(null)

  // Convertir les meetings planifiées en événements FullCalendar
  // Seules les AG planifiées apparaissent avec leurs dates de convocation et PV calculées
  const events = useMemo<EventInput[]>(() => {
    const allEvents: EventInput[] = []

    // Filtrer uniquement les AG planifiées
    const scheduledMeetings = meetings.filter(m => m.status === 'SCHEDULED')

    scheduledMeetings.forEach((meeting) => {
      if (!meeting.meeting_date) return

      const meetingDate = new Date(meeting.meeting_date)
      const residenceName = meeting.residence?.name || 'Résidence inconnue'
      const residenceKey = meeting.residence?.residence_key

      // 1. Événement principal : l'AG planifiée
      const hasTime = !!meeting.start_time
      const formattedTime = meeting.start_time
        ? meeting.start_time.substring(0, 5) // Garder seulement HH:MM
        : null
      const eventTitle = residenceKey ? `[${residenceKey}] ${meeting.title}` : meeting.title

      // Construire la date de début et fin
      let startDate: Date
      let endDate: Date | undefined

      if (hasTime && formattedTime) {
        const timeParts = formattedTime.split(':').map(Number)
        const hours = timeParts[0] ?? 0
        const minutes = timeParts[1] ?? 0
        startDate = new Date(meetingDate)
        startDate.setHours(hours, minutes, 0, 0)
        // Durée par défaut de 2h
        endDate = new Date(startDate)
        endDate.setHours(endDate.getHours() + 2)
      } else {
        startDate = meetingDate
      }

      allEvents.push({
        id: meeting.id,
        title: eventTitle,
        start: startDate,
        end: endDate,
        allDay: !hasTime,
        backgroundColor: EVENT_COLORS.meeting_scheduled.bg,
        borderColor: EVENT_COLORS.meeting_scheduled.border,
        textColor: '#ffffff',
        extendedProps: {
          type: 'meeting',
          status: meeting.status,
          residenceName,
          residenceKey,
          meetingId: meeting.id,
          startTime: formattedTime,
        },
      })

      // 2. Date limite d'envoi des convocations (21 jours avant l'AG)
      const convocationDeadline = new Date(meetingDate)
      convocationDeadline.setDate(convocationDeadline.getDate() - CONVOCATION_DAYS_BEFORE)
      const convocationDateStr = convocationDeadline.toISOString().split('T')[0]

      allEvents.push({
        id: `${meeting.id}-convocation`,
        title: `CONVOC ${residenceKey || residenceName}`,
        start: convocationDateStr,
        allDay: true,
        backgroundColor: EVENT_COLORS.convocation_deadline.bg,
        borderColor: EVENT_COLORS.convocation_deadline.border,
        textColor: '#ffffff',
        display: 'block',
        extendedProps: {
          type: 'convocation_deadline',
          meetingId: meeting.id,
          meetingTitle: meeting.title,
          residenceName,
          residenceKey,
        },
      })

      // 3. Date d'envoi du PV (15 jours après l'AG)
      const pvDeadline = new Date(meetingDate)
      pvDeadline.setDate(pvDeadline.getDate() + PV_DAYS_AFTER)
      const pvDateStr = pvDeadline.toISOString().split('T')[0]

      allEvents.push({
        id: `${meeting.id}-pv`,
        title: `PV ${residenceKey || residenceName}`,
        start: pvDateStr,
        allDay: true,
        backgroundColor: EVENT_COLORS.pv_deadline.bg,
        borderColor: EVENT_COLORS.pv_deadline.border,
        textColor: '#ffffff',
        display: 'block',
        extendedProps: {
          type: 'pv_deadline',
          meetingId: meeting.id,
          meetingTitle: meeting.title,
          residenceName,
          residenceKey,
        },
      })
    })

    return allEvents
  }, [meetings])

  const handleEventClick = (info: EventClickArg) => {
    const meetingId = info.event.extendedProps.meetingId
    if (meetingId) {
      onMeetingClick(meetingId)
    }
  }

  return (
    <Box>
      {/* Légende */}
      <HStack gap={4} mb={4} flexWrap="wrap">
        <HStack gap={1}>
          <Box w={3} h={3} borderRadius="sm" bg={EVENT_COLORS.meeting_scheduled.bg} />
          <Text fontSize="xs" color="gray.600">AG planifiée</Text>
        </HStack>
        <HStack gap={1}>
          <Box w={3} h={3} borderRadius="sm" bg={EVENT_COLORS.convocation_deadline.bg} />
          <Text fontSize="xs" color="gray.600">Convocation (J-21)</Text>
        </HStack>
        <HStack gap={1}>
          <Box w={3} h={3} borderRadius="sm" bg={EVENT_COLORS.pv_deadline.bg} />
          <Text fontSize="xs" color="gray.600">Envoi PV (J+15)</Text>
        </HStack>
      </HStack>

      {/* Calendrier */}
      <Box className="meetings-calendar">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, multiMonthPlugin, listPlugin]}
          initialView="dayGridMonth"
          locale={frLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'multiMonthYear,dayGridMonth,timeGridWeek,listMonth',
          }}
          buttonText={{
            today: "Aujourd'hui",
            month: 'Mois',
            week: 'Semaine',
            year: 'Année',
            list: 'Liste',
          }}
          events={events}
          eventClick={handleEventClick}
          height="auto"
          aspectRatio={1.8}
          fixedWeekCount={false}
          showNonCurrentDates={true}
          navLinks={true}
          dayMaxEvents={3}
          moreLinkText={(num) => `+${num} autres`}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            meridiem: false,
          }}
          // Tooltip au survol
          eventDidMount={(info) => {
            const props = info.event.extendedProps
            let tooltipContent = ''

            if (props.type === 'meeting') {
              tooltipContent = `${props.residenceKey ? `[${props.residenceKey}] ` : ''}${props.residenceName}\n${info.event.title}`
            } else if (props.type === 'convocation_deadline') {
              tooltipContent = `Date limite d'envoi des convocations\n${props.meetingTitle}`
            } else if (props.type === 'pv_deadline') {
              tooltipContent = `Date d'envoi du PV\n${props.meetingTitle}`
            }

            if (tooltipContent) {
              info.el.setAttribute('title', tooltipContent)
            }
          }}
        />
      </Box>
    </Box>
  )
}
